import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectUnits } from '../../store/slices/settings/unitsSlice';
import {
  handleSnowSensorTemp,
  selectSnowSensor,
} from '../../store/slices/settings/snowSensorSlice';
import { fromInputTemp, toDisplayTemp } from '../../../helpers/helpers';
import { logger } from '../../../helpers/logger';
import { snowThresholdWrites } from '../../../helpers/payloads/settingsPayloads';
import { saveFailedMessage, settingsMessage } from './settingsMessage';
import { sendSettingWrites } from './sendSettingWrites';
import { useSettingsDeviceIds } from './useSettingsDeviceIds';

const SNOW_SYSTEMS = ['ess', 'tgs', 'tes'];

const savedKey = (system) => `${system}SnowSensorTemp`;

const bySystem = (pick) =>
  Object.fromEntries(SNOW_SYSTEMS.map((system) => [system, pick(system)]));

/**
 * The confirm text for new snow-sensor triggers. `saved` is `{ ess, tgs,
 * tes }` in °C, `typed` the same shape in the operator's unit; an empty
 * field is left unchanged.
 */
export const reviewSnowSensor = ({ saved, isF, typed }) => {
  if (SNOW_SYSTEMS.every((system) => !typed[system])) {
    return settingsMessage('snow sensor trigger', 'input fields incomplete', [
      'input fields incomplete please field all the input fields.',
    ]);
  }

  const shownSaved = bySystem((system) => toDisplayTemp(+saved[system], isF));
  if (SNOW_SYSTEMS.every((system) => +typed[system] === shownSaved[system])) {
    return settingsMessage('snow sensor trigger', 'input fields unchanged', [
      'input fields set to the same temperature as before.',
    ]);
  }

  const unit = isF ? '°f' : '°c';
  return settingsMessage(
    'snow sensor trigger',
    'change temperature',
    SNOW_SYSTEMS.filter(
      (system) => typed[system] && +typed[system] !== shownSaved[system],
    ).map(
      (system) =>
        `you are about to set the ${system}-snow sensor trigger temperature from ${shownSaved[system]}${unit} to ${typed[system]}${unit}`,
    ),
  );
};

/**
 * The snow-sensor panel: one trigger per system. `review` builds the confirm
 * message; `confirm` sends the changed triggers and stores them only once the
 * devices took them. It resolves to the failure message, or null.
 */
export const useSaveSnowSensor = () => {
  const dispatch = useDispatch();
  const { isF } = useSelector(selectUnits);
  const snowSensor = useSelector(selectSnowSensor);
  const deviceIds = useSettingsDeviceIds();

  const essSnowSensorRef = useRef(null);
  const tgsSnowSensorRef = useRef(null);
  const tesSnowSensorRef = useRef(null);
  const refs = { ess: essSnowSensorRef, tgs: tgsSnowSensorRef, tes: tesSnowSensorRef };
  const typedValues = () => bySystem((system) => refs[system].current.value);
  const saved = bySystem((system) => snowSensor[savedKey(system)]);

  const review = () =>
    reviewSnowSensor({
      saved,
      isF,
      typed: typedValues(),
    });

  const confirm = async () => {
    const typed = typedValues();
    const shownSaved = bySystem((system) =>
      toDisplayTemp(+saved[system], isF),
    );
    const changed = bySystem(
      (system) =>
        Boolean(typed[system]) && +typed[system] !== shownSaved[system],
    );
    const temps = bySystem((system) => fromInputTemp(typed[system], isF));
    try {
      await sendSettingWrites(
        snowThresholdWrites({
          temps,
          essChanged: changed.ess,
          deviceIds: deviceIds(),
        }),
      );
    } catch (error) {
      logger.error('snow sensor save failed', error);
      return saveFailedMessage(
        'snow sensor trigger',
        'change temperature',
        error,
        { asList: true },
      );
    }
    SNOW_SYSTEMS.filter((system) => changed[system]).forEach((system) =>
      dispatch(
        handleSnowSensorTemp({ keyName: savedKey(system), value: temps[system] }),
      ),
    );
    return null;
  };

  return {
    panelProps: { essSnowSensorRef, tgsSnowSensorRef, tesSnowSensorRef },
    review,
    confirm,
  };
};
